/**
 * Labels for Manage Account subscription status.
 * Expiry comes from the backend billing status and display plan.
 * Organization status INACTIVE is not treated as expired.
 */
export type SubscriptionAccessFields = {
  planType?: string;
  displayPlan?: string;
  status?: string;
  subscriptionStatus?: string;
  subscriptionBillingStatus?: string;
};

export type SubscriptionAccessView = {
  expired: boolean;
  cancelled: boolean;
  badgeLabel: string;
  accessPeriodPrefix: 'Access ended' | 'Access until' | 'Valid until';
  activityMessage: string;
};

export function resolveSubscriptionAccessView(
  sub: SubscriptionAccessFields | null | undefined,
  planLabel: string
): SubscriptionAccessView {
  const display = (sub?.displayPlan || '').toUpperCase();
  const plan = (sub?.planType || '').toUpperCase();
  const billing = (sub?.subscriptionBillingStatus || '').toUpperCase();
  const stripeStatus = (sub?.subscriptionStatus || '').toLowerCase();
  const isFree = display === 'FREE' || plan === 'FREE';

  const billingKnown = billing === 'ACTIVE' || billing === 'CANCELLED' || billing === 'EXPIRED' || billing === 'PAST_DUE';
  const expired = !isFree && (billing === 'EXPIRED' || (!billingKnown && display === 'EXPIRED'));
  const cancelled =
    !expired &&
    !isFree &&
    (billing === 'CANCELLED' || (!billingKnown && (display === 'CANCELLED' || stripeStatus === 'canceled')));

  let badgeLabel: string;
  if (cancelled) {
    badgeLabel = 'Cancelled';
  } else if (expired) {
    badgeLabel = 'Expired';
  } else if (isFree) {
    badgeLabel = 'Active';
  } else {
    const raw = (sub?.status || sub?.subscriptionStatus || 'ACTIVE').toUpperCase();
    badgeLabel = raw === 'INACTIVE' || raw === 'ACTIVE' ? 'Active' : raw;
  }

  const accessPeriodPrefix: SubscriptionAccessView['accessPeriodPrefix'] = expired
    ? 'Access ended'
    : cancelled
      ? 'Access until'
      : 'Valid until';

  const activityMessage = expired
    ? `Previous plan: ${planLabel} — subscription ended`
    : cancelled
      ? `Plan: ${planLabel} (cancelled; access until period end)`
      : `Current plan: ${planLabel}`;

  return { expired, cancelled, badgeLabel, accessPeriodPrefix, activityMessage };
}
